import { Pressable, StyleSheet, Text, type StyleProp, type ViewStyle } from "react-native";

import { Icon } from "../Icon/Icon";
import { webTapFloor, type UiTheme } from "../theme";
import { stateAria } from "../a11y";

// THE «create» affordance (#227, CN-18, decision 11/08): a dashed frame that
// reads as an empty slot to fill. «Add target» (weekly targets) and the empty
// focus card (no exercise yet) are different gestures, but they must look
// alike, so the frame lives here once.
//
// The dashed outline is the shape cue; the "+" and the label carry the meaning
// (never colour alone). The frame uses `borderStrong`: `border` is ~1.3:1 and
// would not mark the boundary. ≥44pt: `webTapFloor` on web (RNW drops hitSlop), 40 plus
// hitSlop 8 on native.

/** The dashed «create» frame as a style, for a container that is itself the
 *  empty slot (EmptyFocusCard) and not a tappable row. */
export function createFrameStyle(theme: UiTheme) {
  const { palette: p, radius: r } = theme;
  return {
    borderWidth: 1,
    borderColor: p.borderStrong,
    borderStyle: "dashed",
    borderRadius: r.r3,
  } as const;
}

export function CreateRow({
  theme,
  label,
  onPress,
  accessibilityLabel,
  disabled = false,
  style,
}: {
  theme: UiTheme;
  label: string;
  onPress: () => void;
  accessibilityLabel?: string;
  disabled?: boolean;
  /** Outer spacing from the caller (margin). */
  style?: StyleProp<ViewStyle>;
}) {
  const styles = makeStyles(theme);
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityState={{ disabled: disabled || undefined }}
      {...stateAria({ disabled: disabled || undefined })}
      disabled={disabled}
      onPress={onPress}
      hitSlop={8}
      style={({ pressed }) => [styles.row, style, (pressed || disabled) && styles.dim]}
    >
      <Icon name="plus" size={theme.typography.sizes.md} color={theme.palette.brand500} />
      <Text style={styles.text}>{label}</Text>
    </Pressable>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: t } = theme;
  return StyleSheet.create({
    row: {
      ...createFrameStyle(theme),
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: s.s2,
      minHeight: webTapFloor ?? 40,
      paddingHorizontal: s.s3,
    },
    text: { color: p.brand500, fontSize: t.sizes.base, fontWeight: "700" },
    dim: { opacity: 0.7 },
  });
}
